import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { addDays, weekdayOf } from '@kraftwerk/shared';
import type { IsoDate } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalJournalNote } from '../../db/local.js';
import { groupTimeline, hasCheckin, journalTimeline } from '../../db/diary.js';
import type { TimelineDay } from '../../db/diary.js';
import { memoriesFor, searchNotes } from '../../db/notes.js';
import type { Memory } from '../../db/notes.js';
import { listBucket } from '../../db/bucket.js';
import { useIsChild, useModules } from '../../hooks/useModules.js';
import { useTimeZone } from '../../hooks/useToday.js';
import { formatDay } from '../../format.js';
import { NoteCard } from './DayNotes.js';
import { PhotoImage } from './JournalPhotos.js';
import { BucketPrompts } from './BucketList.js';

/** Wohin ein geöffneter Tag gleich springt — Befinden, neuer Gedanke, Fotos. */
export type DayFocus = 'checkin' | 'note' | 'photo';

/** So viele Wochen zeigt „Tage“ zuerst; „Ältere Wochen“ holt je so viele nach. */
const WEEKS_STEP = 4;

const MOOD_DOT = ['', 'bg-good/20', 'bg-good/35', 'bg-good/55', 'bg-good/75', 'bg-good'];

/**
 * „Tage“ im Tagebuch (20.10.2026).
 *
 * Vorher: oben „Erlebt?“, Gewicht und ein großer Knopf „Heute eintragen“
 * ohne Zusammenhang, darunter nur die Tage **mit Befinden** als Zahlen —
 * ein Tag mit nur einem Gedanken oder nur Training fehlte. Jetzt: eine
 * Heute-Karte mit drei Wegen (Befinden, Gedanke, Foto), darunter jeder Tag
 * nach Wochen, mit dem, was war. Leere Tage werden zusammengefasst statt
 * ausgelassen — ein fehlender Tag sähe aus wie ein vergessener.
 */
export function JournalDays({
  today,
  onOpen,
}: {
  today: string;
  onOpen: (date: string, focus?: DayFocus) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const child = useIsChild();
  const modules = useModules();
  const [weeks, setWeeks] = useState(WEEKS_STEP);
  const [searching, setSearching] = useState(false);
  // Ab dem Montag vor `weeks` Wochen — ganze Wochen, keine angeschnittene.
  const monday = addDays(today as IsoDate, 1 - weekdayOf(today));
  const from = addDays(monday, -7 * (weeks - 1));
  const days = useLiveQuery(
    () => (timeZone ? journalTimeline(from, today, timeZone, db) : undefined),
    [from, today, timeZone],
  );
  const older = useLiveQuery(() => hasOlder(from), [from], false);
  const bucket = useLiveQuery(() => listBucket(db), [], []);
  const anyNote = useLiveQuery(
    async () => (await db.journal_notes.toArray()).some((note) => !note.deletedAt),
    [],
    false,
  );

  const weekLabel = (start: string) => {
    if (start === monday) return t('journal.days.thisWeek');
    if (start === addDays(monday, -7)) return t('journal.days.lastWeek');
    return t('journal.days.weekRange', {
      from: formatDay(start, 'date', i18n.language),
      to: formatDay(addDays(start as IsoDate, 6), 'date', i18n.language),
    });
  };
  const todayDay = days?.[0];
  const groups = days ? groupTimeline(days.slice(1)) : [];

  return (
    <section aria-labelledby="journal-days-heading" className="flex flex-col gap-3">
      <h2 id="journal-days-heading" className="sr-only">
        {t('journal.tabs.days')}
      </h2>

      {todayDay ? (
        <TodayCard
          day={todayDay}
          child={child}
          onOpen={onOpen}
          searchButton={
            anyNote ? (
              <button
                type="button"
                aria-expanded={searching}
                aria-label={t('notes.search')}
                onClick={() => setSearching((value) => !value)}
                className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
              >
                <span aria-hidden="true">⌕</span>
              </button>
            ) : null
          }
        />
      ) : null}

      {searching && anyNote ? <NoteSearch onOpen={onOpen} /> : null}

      <Memories today={today} onOpen={onOpen} />
      <BucketPrompts items={bucket} today={today} onOpen={onOpen} />

      {groups.map((group) => (
        <section
          key={group.monday}
          aria-labelledby={`week-${group.monday}`}
          className="flex flex-col gap-2"
        >
          <h3 id={`week-${group.monday}`} className="kw-kicker mt-1">
            {weekLabel(group.monday)}
          </h3>
          <ul className="flex flex-col gap-2">
            {group.items.map((item) =>
              item.kind === 'empty' ? (
                <li key={`empty-${item.to}`} className="px-1 text-xs text-ink-muted">
                  {item.count === 1
                    ? t('journal.days.emptyOne', {
                        day: formatDay(item.to, 'short', i18n.language),
                      })
                    : t('journal.days.emptyRun', {
                        count: item.count,
                        from: formatDay(item.from, 'short', i18n.language),
                        to: formatDay(item.to, 'short', i18n.language),
                      })}
                </li>
              ) : (
                <li key={item.day.date}>
                  <DayRow day={item.day} child={child} modules={modules} onOpen={onOpen} />
                </li>
              ),
            )}
          </ul>
        </section>
      ))}

      {older ? (
        <button
          type="button"
          onClick={() => setWeeks((value) => value + WEEKS_STEP)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('journal.days.older')}
        </button>
      ) : null}
    </section>
  );
}

/** Heute: was schon da ist, und drei Wege, etwas dazuzuschreiben. */
function TodayCard({
  day,
  child,
  onOpen,
  searchButton,
}: {
  day: TimelineDay;
  child: boolean;
  onOpen: (date: string, focus?: DayFocus) => void;
  searchButton: React.ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const checkin = checkinParts(day, child, t, i18n.language);
  const extras = [
    day.notes > 0 ? t('notes.count', { count: day.notes }) : null,
    day.photos > 0 ? t('journal.summary.photos', { count: day.photos }) : null,
  ].filter((part): part is string => part !== null);

  return (
    <section aria-labelledby="journal-today" className="kw-tile flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpen(day.date)}
          className="flex min-w-0 flex-col gap-0.5 text-left"
        >
          <h3 id="journal-today" className="kw-kicker">
            {t('journal.today')} · {formatDay(day.date, 'long', i18n.language)}
          </h3>
          <span className="text-sm">
            {checkin.length > 0 ? checkin.join(' · ') : t('journal.days.noCheckin')}
          </span>
          {extras.length > 0 ? (
            <span className="text-xs text-ink-muted">{extras.join(' · ')}</span>
          ) : null}
        </button>
        {searchButton}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => onOpen(day.date, 'checkin')}
          className={`kw-btn kw-btn-small px-2 ${hasCheckin(day.entry) ? 'kw-btn-quiet' : ''}`}
        >
          {t('journal.days.checkin')}
        </button>
        <button
          type="button"
          onClick={() => onOpen(day.date, 'note')}
          className="kw-btn kw-btn-quiet kw-btn-small px-2"
        >
          {t('journal.days.note')}
        </button>
        <button
          type="button"
          onClick={() => onOpen(day.date, 'photo')}
          className="kw-btn kw-btn-quiet kw-btn-small px-2"
        >
          {t('journal.days.photo')}
        </button>
      </div>
    </section>
  );
}

/** Ein vergangener Tag: Stimmung als Punkt, Befinden kurz, was war, Gedanke, Bild. */
function DayRow({
  day,
  child,
  modules,
  onOpen,
}: {
  day: TimelineDay;
  child: boolean;
  modules: readonly string[];
  onOpen: (date: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const mood = day.entry?.mood ?? null;
  const checkin = checkinParts(day, child, t, i18n.language);
  const story = [
    ...day.bucketDone.map((title) => t('journal.days.bucket', { title })),
    ...(modules.includes('training') && day.trainings.length > 0
      ? [t('journal.days.training', { list: day.trainings.join(', ') })]
      : []),
    ...(modules.includes('nutrition') && day.kcal !== null
      ? [t('journal.days.kcal', { kcal: day.kcal })]
      : []),
    ...(modules.includes('nutrition') && day.waterMl > 0
      ? [
          t('journal.days.water', {
            liters: (day.waterMl / 1000).toLocaleString(i18n.language, {
              maximumFractionDigits: 2,
            }),
          }),
        ]
      : []),
    ...(modules.includes('planning') && day.events > 0
      ? [t('journal.days.events', { count: day.events })]
      : []),
  ];

  return (
    <button
      type="button"
      onClick={() => onOpen(day.date)}
      className="kw-tile flex w-full items-start gap-3 text-left"
    >
      <span
        aria-hidden="true"
        className={`mt-1.5 size-3 shrink-0 rounded-full border border-line ${
          mood !== null ? MOOD_DOT[mood] : ''
        }`}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="font-medium">{formatDay(day.date, 'long', i18n.language)}</span>
        {mood !== null ? (
          <span className="sr-only">{t('diary.moodValue', { value: mood })}</span>
        ) : null}
        {checkin.length > 0 ? (
          <span className="text-xs text-ink-muted">{checkin.join(' · ')}</span>
        ) : null}
        {story.length > 0 ? (
          <span className="text-xs text-ink-muted">{story.join(' · ')}</span>
        ) : null}
        {day.firstNote ? (
          <span className="line-clamp-2 text-sm">
            {day.firstNote.title ? <strong>{day.firstNote.title} </strong> : null}
            {day.firstNote.body}
          </span>
        ) : day.entry?.note ? (
          <span className="line-clamp-2 text-sm">{day.entry.note}</span>
        ) : null}
        {day.notes > 1 ? (
          <span className="text-xs text-ink-muted">{t('notes.count', { count: day.notes })}</span>
        ) : null}
      </span>
      {day.firstPhoto ? (
        <span className="relative size-14 shrink-0 overflow-hidden rounded-lg">
          <PhotoImage photo={day.firstPhoto} />
          {day.photos > 1 ? (
            <span className="absolute right-0.5 bottom-0.5 rounded bg-surface/80 px-1 text-[10px]">
              {day.photos}
            </span>
          ) : null}
        </span>
      ) : null}
    </button>
  );
}

/** Das Befinden in kurzen Worten — Stimmung steht als Punkt, hier der Rest. */
function checkinParts(day: TimelineDay, child: boolean, t: TFunction, language: string): string[] {
  const entry = day.entry;
  if (!entry) return [];
  return [
    entry.mood === null ? null : t('journal.summary.mood', { value: entry.mood }),
    entry.sleepHours === null
      ? null
      : t('journal.summary.sleep', { hours: entry.sleepHours.toLocaleString(language) }),
    entry.energy === null ? null : t('journal.summary.energy', { value: entry.energy }),
    child || typeof entry.bodyWeightKg !== 'number'
      ? null
      : t('journal.summary.weight', {
          kg: entry.bodyWeightKg.toLocaleString(language, { maximumFractionDigits: 1 }),
        }),
    // Körpermaße (23.10.2026): nur dass gemessen wurde — die Werte stehen im Tag.
    child || !entry.measurements ? null : t('journal.measurements.measured'),
    typeof entry.stepCount === 'number'
      ? t('journal.steps.value', { steps: entry.stepCount.toLocaleString(language) })
      : null,
  ].filter((part): part is string => part !== null);
}

/** Gibt es vor `from` überhaupt noch etwas zu zeigen? */
async function hasOlder(from: string): Promise<boolean> {
  const [entry, note] = await Promise.all([
    db.journal_entries.where('date').below(from).first(),
    db.journal_notes.where('date').below(from).first(),
  ]);
  return entry !== undefined || note !== undefined;
}

/** Suche über alle Gedanken — Titel, Text, Schlagwörter. Hinter der Lupe. */
function NoteSearch({ onOpen }: { onOpen: (date: string) => void }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const inputId = useId();
  const [query, setQuery] = useState('');
  const results = useLiveQuery(
    () => (query.trim().length >= 2 ? searchNotes(query, db) : Promise.resolve(null)),
    [query],
    null,
  ) as LocalJournalNote[] | null;
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={inputId} className="sr-only">
        {t('notes.search')}
      </label>
      <input
        id={inputId}
        type="search"
        value={query}
        // Wer die Lupe antippt, will tippen.
        autoFocus
        placeholder={t('notes.searchShort')}
        aria-describedby={`${inputId}-hint`}
        onChange={(event) => setQuery(event.target.value)}
        className="kw-field"
      />
      <span id={`${inputId}-hint`} className="sr-only">
        {t('notes.searchHint')}
      </span>
      {results ? (
        results.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('notes.noResults')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {results.map((note) => (
              <li key={note.id}>
                <button
                  type="button"
                  onClick={() => onOpen(note.date)}
                  className="kw-tile flex w-full flex-col gap-1 text-left"
                >
                  <span className="text-xs text-ink-muted">
                    {formatDay(note.date, 'full', i18n.language)} · {note.time}
                  </span>
                  {note.title ? <span className="font-medium">{note.title}</span> : null}
                  <span className="line-clamp-2 text-sm">{note.body}</span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

/** „An diesem Tag" — nur, wenn es etwas gibt. */
function Memories({
  today,
  onOpen,
}: {
  today: string;
  onOpen: (date: string) => void;
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const memories = useLiveQuery(() => memoriesFor(today, db), [today], [] as Memory[]);
  if (memories.length === 0) return null;
  return (
    <section aria-labelledby="memories-heading" className="flex flex-col gap-2">
      <h3 id="memories-heading" className="kw-kicker">
        {t('notes.memories')}
      </h3>
      {memories.map((memory) => (
        <div key={memory.date} className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => onOpen(memory.date)}
            className="self-start text-sm font-semibold text-accent underline"
          >
            {memory.kind === 'month'
              ? t('notes.monthAgo')
              : t('notes.yearsAgo', { count: memory.ago })}{' '}
            · {formatDay(memory.date, 'full', i18n.language)}
          </button>
          {memory.photos.length > 0 ? (
            <ul className="grid grid-cols-3 gap-2">
              {memory.photos.slice(0, 6).map((photo) => (
                <li key={photo.id}>
                  <PhotoImage photo={photo} />
                </li>
              ))}
            </ul>
          ) : null}
          {memory.notes.slice(0, 2).map((note) => (
            <NoteCard key={note.id} note={note} />
          ))}
        </div>
      ))}
    </section>
  );
}
